import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import NotificationBell from "./NotificationBell";
import ImageViewer from "./ImageViewer";
import pickleballIcon from "./pickleball.png";
import MatchResult from "./MatchResult";
import MannerReview from "./MannerReview";
import { LevelBadge, MannerBadge, SportLevelBadge } from "./Badges";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";


const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const STATUS_LABEL = {
  pending:      { text: "대기중",    color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  accepted:     { text: "수락됨",    color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected:     { text: "거절됨",    color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb" },
  cancelled:    { text: "취소됨",    color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb" },
  auto_rejected:{ text: "자동 거절됨", color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb" },
};

const WEEK_DAYS = ["일","월","화","수","목","금","토"];

function formatGameDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("ko-KR", { month: "long", day: "numeric" }) + ` (${WEEK_DAYS[d.getDay()]})`;
}

function buildLocation(post) {
  if (!post) return null;
  const loc = [post.location_city, post.location_district, post.location_dong, post.location_name]
    .filter(Boolean).join(" ");
  return loc || null;
}

// 24시간(ms) 상수
const H24 = 24 * 60 * 60 * 1000;

export default function MyMatches({ onNavigateToChat, initialTab, onHome }) {
  const { user, activeTeam } = useAuth();

  const [activeTab,     setActiveTab]     = useState(initialTab || "accepted");
  const [receivedGroups, setReceivedGroups] = useState([]); // [{type, postId, post, apps}]
  const [sent,          setSent]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [chatLoading,   setChatLoading]   = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null); // { appId, isReceived }
  const [resultMap,     setResultMap]     = useState({});   // appId → match_result row
  const [reviewedSet,   setReviewedSet]   = useState(new Set()); // appId 집합 (이미 매너평가 한 것)
  const [resultTarget,  setResultTarget]  = useState(null); // 결과 입력/확인 오버레이용
  const [resultRefreshKey, setResultRefreshKey] = useState(0); // Realtime 변경 시 MatchResult 리마운트
  const [reviewTarget,  setReviewTarget]  = useState(null); // 매너 평가 오버레이용
  const [scheduleMap,   setScheduleMap]   = useState({});   // appId → schedule row
  const [confirmDelete, setConfirmDelete] = useState(null); // { appId, isReceived }
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  // ── 회원 신청 관리 ──
  const [clubApps, setClubApps] = useState([]);
  const [clubSentApps, setClubSentApps] = useState([]);
  const [clubLoading, setClubLoading] = useState(false);
  const [clubActionLoading, setClubActionLoading] = useState(null);

  // ── 팀원 가입 신청 (team_members) ──
  const [memberApps, setMemberApps] = useState([]);
  const [memberActionLoading, setMemberActionLoading] = useState(null);

  const fetchClubApps = useCallback(async () => {
    if (!activeTeam?.id) return;
    setClubLoading(true);

    // 1. 내가 올린 모집글 ID 목록 조회
    const { data: myRecruitments } = await supabase
      .from("club_recruitments")
      .select("id, title")
      .eq("team_id", activeTeam.id);

    if (!myRecruitments || myRecruitments.length === 0) {
      setClubApps([]);
      setClubLoading(false);
      return;
    }

    const recruitmentIds = myRecruitments.map((r) => r.id);
    const titleMap = Object.fromEntries(myRecruitments.map((r) => [r.id, r.title]));

    // 2. 해당 모집글에 달린 신청 조회
    const { data } = await supabase
      .from("club_applications")
      .select("*")
      .in("recruitment_id", recruitmentIds)
      .order("created_at", { ascending: false });

    // 모집글 제목 붙여주기
    const apps = (data || []).map((a) => ({ ...a, recruitment_title: titleMap[a.recruitment_id] || "" }));
    setClubApps(apps);

    // 내가 보낸 회원 신청 조회
    if (user?.id) {
      const { data: sentData } = await supabase
        .from("club_applications")
        .select("*")
        .eq("applicant_user_id", user.id)
        .order("created_at", { ascending: false });

      // 모집글 제목 가져오기
      const sentRecruitIds = [...new Set((sentData || []).map((a) => a.recruitment_id).filter(Boolean))];
      let sentTitleMap = {};
      if (sentRecruitIds.length > 0) {
        const { data: recs } = await supabase.from("club_recruitments").select("id, title, team_name").in("id", sentRecruitIds);
        sentTitleMap = Object.fromEntries((recs || []).map((r) => [r.id, { title: r.title, team_name: r.team_name }]));
      }
      setClubSentApps((sentData || []).map((a) => ({
        ...a,
        recruitment_title: sentTitleMap[a.recruitment_id]?.title || "",
        recruitment_team_name: sentTitleMap[a.recruitment_id]?.team_name || "",
      })));
    }

    setClubLoading(false);
  }, [activeTeam?.id, user?.id]);

  // 팀원 가입 신청 조회 (team_members pending)
  const fetchMemberApps = useCallback(async () => {
    if (!user?.id) return;

    try {
      // 1. 내가 소유한 팀 목록
      const { data: myTeams, error: teamsErr } = await supabase
        .from("teams")
        .select("id, team_name")
        .eq("user_id", user.id);
      if (teamsErr || !myTeams || myTeams.length === 0) { setMemberApps([]); return; }

      const teamIds = myTeams.map((t) => t.id);
      const teamNameMap = Object.fromEntries(myTeams.map((t) => [t.id, t.team_name]));

      // 2. team_members에서 pending 조회 (profiles join 없이)
      const { data: rows } = await supabase
        .from("team_members")
        .select("id, team_id, user_id, role, status, nickname, created_at")
        .in("team_id", teamIds)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (!rows || rows.length === 0) { setMemberApps([]); return; }

      // 3. nickname 없는 행은 user_profiles에서 보완
      const missingIds = [...new Set(rows.filter(r => !r.nickname).map(r => r.user_id))];
      let upNickMap = {};
      if (missingIds.length > 0) {
        const { data: upData } = await supabase
          .from("user_profiles")
          .select("id, nickname")
          .in("id", missingIds);
        upNickMap = Object.fromEntries((upData || []).map(p => [p.id, p.nickname]));
      }

      setMemberApps(rows.map((m) => ({
        ...m,
        resolvedNick: m.nickname || upNickMap[m.user_id] || "알 수 없음",
        team_name: teamNameMap[m.team_id] || "",
      })));
    } catch (e) {
      setMemberApps([]);
    }
  }, [user?.id]);

  const handleMemberAccept = async (id) => {
    setMemberActionLoading(id);
    await supabase.from("team_members").update({ status: "accepted" }).eq("id", id);
    setMemberActionLoading(null);
    fetchMemberApps();
  };

  const handleMemberReject = async (id) => {
    setMemberActionLoading(id);
    await supabase.from("team_members").update({ status: "rejected" }).eq("id", id);
    setMemberActionLoading(null);
    fetchMemberApps();
  };

  useEffect(() => { fetchClubApps(); fetchMemberApps(); }, [fetchClubApps, fetchMemberApps]);

  const handleClubAccept = async (app) => {
    setClubActionLoading(app.id);
    await supabase.from("club_applications").update({ status: "accepted" }).eq("id", app.id);

    const applicantDisplayName = app.applicant_email ? app.applicant_email.split("@")[0] : (app.applicant_name || "신청자");

    // 기존 채팅방 확인 (중복 방지)
    const { data: existing } = await supabase
      .from("chat_rooms")
      .select("*")
      .eq("match_id", app.id)
      .maybeSingle();

    let room = existing;
    if (!room) {
      const { data: newRoom } = await supabase
        .from("chat_rooms")
        .insert([{
          match_id: app.id,
          team1_name: applicantDisplayName,
          team2_name: activeTeam.team_name,
          sports_type: activeTeam.sports_type || null,
        }])
        .select().single();
      room = newRoom;
    }

    // 신청자에게 알림
    if (app.applicant_user_id) {
      await supabase.from("notifications").insert([{
        user_id: app.applicant_user_id,
        type: "match_accepted",
        title: "🎉 회원 가입이 수락됐어요!",
        body: "가입 신청이 수락됐어요. 채팅방을 확인하세요",
      }]);
    }

    setClubActionLoading(null);
    fetchClubApps();
    if (room && onNavigateToChat) onNavigateToChat(room);
  };

  const handleClubChat = async (app) => {
    setClubActionLoading(app.id);
    const { data: room } = await supabase
      .from("chat_rooms")
      .select("*")
      .eq("match_id", app.id)
      .maybeSingle();
    setClubActionLoading(null);
    if (room && onNavigateToChat) onNavigateToChat(room);
    else alert("채팅방을 찾을 수 없어요");
  };

  const handleClubReject = async (appId) => {
    setClubActionLoading(appId);
    await supabase.from("club_applications").update({ status: "rejected" }).eq("id", appId);
    setClubActionLoading(null);
    fetchClubApps();
  };

  const fetchApplications = useCallback(async () => {
    if (!activeTeam?.id) return;
    setLoading(true);
    setError("");

    const [recRes, sentRes] = await Promise.all([
      supabase.from("applications").select("*").eq("host_team_id", activeTeam.id).order("created_at", { ascending: false }),
      supabase.from("applications").select("*").eq("guest_team_id", activeTeam.id).order("created_at", { ascending: false }),
    ]);

    if (recRes.error || sentRes.error) {
      setError("데이터를 불러오지 못했어요. 잠시 후 다시 시도해주세요.");
      setLoading(false);
      return;
    }

    const recData  = recRes.data  || [];
    const sentData = sentRes.data || [];

    // ── 상대 팀 ID 수집 ──
    const guestIds  = [...new Set(recData.map((a) => a.guest_team_id).filter(Boolean))];
    const targetIds = [...new Set(sentData.map((a) => a.host_team_id).filter(Boolean))];
    const allIds    = [...new Set([...guestIds, ...targetIds])];

    let teamMap = {};
    if (allIds.length > 0) {
      const { data: teamData } = await supabase
        .from("teams").select("id, team_name, sports_type, level").in("id", allIds);
      (teamData || []).forEach((t) => { teamMap[t.id] = t; });
    }

    // ── post 데이터 수집 ──
    const postIds = [...new Set([
      ...recData.map((a) => a.post_id),
      ...sentData.map((a) => a.post_id),
    ].filter(Boolean))];

    let postMap = {};
    if (postIds.length > 0) {
      const { data: postsData } = await supabase
        .from("posts").select("*").in("id", postIds);
      (postsData || []).forEach((p) => { postMap[p.id] = p; });
    }

    // ── 받은 신청: post_id 기준으로 그룹화 (전체 status 유지 — 탭별 필터링은 렌더링 시점에)
    const groups = {};
    recData.forEach((app) => {
      const key = app.post_id ? `post-${app.post_id}` : `direct-${app.id}`;
      if (!groups[key]) {
        groups[key] = {
          type:   app.post_id ? "post" : "direct",
          postId: app.post_id || null,
          post:   app.post_id ? (postMap[app.post_id] || null) : null,
          apps:   [],
        };
      }
      groups[key].apps.push({ ...app, guestTeam: teamMap[app.guest_team_id] || null });
    });

    // 각 그룹의 최신 app.created_at 기준으로 정렬
    const groupList = Object.values(groups).sort((a, b) => {
      const latestA = Math.max(...a.apps.map((x) => new Date(x.created_at)));
      const latestB = Math.max(...b.apps.map((x) => new Date(x.created_at)));
      return latestB - latestA;
    });

    setReceivedGroups(groupList);

    // ── 보낸 신청 (전체 status 유지 — 탭별 필터링은 렌더링 시점에)
    setSent(sentData.map((a) => ({
      ...a,
      targetTeam: teamMap[a.host_team_id] || null,
      post:       a.post_id ? (postMap[a.post_id] || null) : null,
    })));

    // ── 수락된 매칭의 경기 결과 / 매너 리뷰 조회 ──
    const acceptedIds = [...recData, ...sentData]
      .filter((a) => a.status === "accepted")
      .map((a) => a.id);

    if (acceptedIds.length > 0) {
      const [{ data: results }, { data: reviews }] = await Promise.all([
        supabase.from("match_results").select("*").in("match_id", acceptedIds),
        supabase.from("manner_reviews").select("match_id")
          .eq("reviewer_team_id", activeTeam.id).in("match_id", acceptedIds),
      ]);
      const rm = {};
      results?.forEach((r) => { rm[r.match_id] = r; });
      setResultMap(rm);
      setReviewedSet(new Set(reviews?.map((r) => r.match_id) || []));
    } else {
      setResultMap({});
      setReviewedSet(new Set());
    }

    // ── 수락된 매칭의 일정 조회 ──
    const acceptedAppIds = [...recData, ...sentData]
      .filter((a) => a.status === "accepted")
      .map((a) => a.id);

    if (acceptedAppIds.length > 0) {
      const { data: rooms } = await supabase
        .from("chat_rooms").select("id, match_id").in("match_id", acceptedAppIds);
      if (rooms?.length > 0) {
        const roomToMatch = {};
        rooms.forEach((r) => { roomToMatch[r.id] = r.match_id; });
        const { data: schedules } = await supabase
          .from("schedules").select("room_id, game_date, game_time, location")
          .in("room_id", rooms.map((r) => r.id));
        const sm = {};
        schedules?.forEach((s) => {
          const matchId = roomToMatch[s.room_id];
          if (matchId) sm[matchId] = s;
        });
        setScheduleMap(sm);
      } else {
        setScheduleMap({});
      }
    } else {
      setScheduleMap({});
    }

    setLoading(false);
  }, [activeTeam]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  // 알림 클릭으로 initialTab이 바뀌면 탭 동기화
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  // match_results Realtime — resultTarget이 열린 동안만 구독
  useEffect(() => {
    const matchId = resultTarget?.app?.id;
    if (!matchId) return;
    const ch = supabase
      .channel(`score_my_${matchId}`)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "match_results",
        filter: `match_id=eq.${matchId}`,
      }, () => {
        setResultRefreshKey((k) => k + 1);
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [resultTarget?.app?.id]);

  // ── 수락/거절 ──
  const handleStatusUpdate = async (appId, newStatus) => {
    setActionLoading(appId);
    const { error } = await supabase.from("applications").update({ status: newStatus }).eq("id", appId);
    if (!error) {
      const app = receivedGroups.flatMap((g) => g.apps).find((a) => a.id === appId);
      if (app?.guest_team_id) {
        const isAccepted = newStatus === "accepted";
        await supabase.from("notifications").insert([{
          team_id:    app.guest_team_id,
          type:       isAccepted ? "match_accepted" : "match_rejected",
          title:      isAccepted ? "🎉 매칭이 수락됐어요!" : "매칭 신청 결과",
          body:       isAccepted
            ? `${activeTeam.team_name} 팀이 매칭을 수락했어요`
            : `${activeTeam.team_name} 팀이 매칭을 거절했어요`,
          related_id: appId,
        }]);
      }
      // 수락 시 채팅방 자동 생성 → 바로 채팅방으로 이동
      if (newStatus === "accepted" && app) {
        let room = null;
        const { data: existing } = await supabase
          .from("chat_rooms").select("*").eq("match_id", appId).maybeSingle();
        if (existing) {
          room = existing;
        } else {
          const { data: newRoom } = await supabase.from("chat_rooms").insert([{
            match_id:    appId,
            team1_name:  app.guestTeam?.team_name || "상대 팀",
            team2_name:  activeTeam.team_name,
            sports_type: app.guestTeam?.sports_type || null,
          }]).select().single();
          room = newRoom;
        }
        setReceivedGroups((prev) => prev.map((g) => ({
          ...g,
          apps: g.apps.map((a) => a.id === appId ? { ...a, status: newStatus } : a),
        })));
        setActionLoading(null);
        if (room && onNavigateToChat) {
          onNavigateToChat(room);
        }
        return;
      }
      setReceivedGroups((prev) => prev.map((g) => ({
        ...g,
        apps: g.apps.map((a) => a.id === appId ? { ...a, status: newStatus } : a),
      })));
    }
    setActionLoading(null);
  };

  // ── 취소 확인 모달 열기 ──
  const handleCancel = (appId, isReceived) => {
    setConfirmCancel({ appId, isReceived });
  };

  // ── 실제 취소 처리 ──
  const doCancel = async () => {
    if (!confirmCancel) return;
    const { appId, isReceived } = confirmCancel;
    setConfirmCancel(null);
    setActionLoading(appId);

    // 1. applications 취소
    const { error } = await supabase.from("applications").update({ status: "cancelled" }).eq("id", appId);
    if (!error) {
      // 2. 해당 매칭의 채팅방 조회
      const { data: chatRoom } = await supabase
        .from("chat_rooms").select("id").eq("match_id", appId).maybeSingle();

      // 3. 채팅방 삭제 (cascade로 messages, schedules 자동 삭제)
      if (chatRoom?.id) {
        await supabase.from("chat_rooms").delete().eq("id", chatRoom.id);
      }

      // 4. 상대방 알림 전송
      let targetTeamId;
      if (isReceived) {
        const app = receivedGroups.flatMap((g) => g.apps).find((a) => a.id === appId);
        targetTeamId = app?.guest_team_id;
      } else {
        const app = sent.find((a) => a.id === appId);
        targetTeamId = app?.host_team_id;
      }
      if (targetTeamId) {
        await supabase.from("notifications").insert([{
          team_id:    targetTeamId,
          type:       "match_cancelled",
          title:      "매칭이 취소됐어요",
          body:       "상대팀이 매칭을 취소했습니다.",
          related_id: appId,
        }]);
      }

      // 5. 화면 상태 업데이트
      if (isReceived) {
        setReceivedGroups((prev) => prev.map((g) => ({
          ...g,
          apps: g.apps.map((a) => a.id === appId ? { ...a, status: "cancelled" } : a),
        })));
      } else {
        setSent((prev) => prev.map((a) => a.id === appId ? { ...a, status: "cancelled" } : a));
      }
    }
    setActionLoading(null);
  };

  // ── 삭제 확인 모달 ──
  const handleDelete = (appId, isReceived, title, body) => {
    setConfirmDelete({ appId, isReceived, title, body });
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { appId, isReceived } = confirmDelete;
    setConfirmDelete(null);
    await supabase.from("applications").delete().eq("id", appId);
    if (isReceived) {
      setReceivedGroups((prev) =>
        prev.map((g) => ({ ...g, apps: g.apps.filter((a) => a.id !== appId) }))
            .filter((g) => g.apps.length > 0)
      );
    } else {
      setSent((prev) => prev.filter((a) => a.id !== appId));
    }
  };

  // ── 채팅하기 ──
  const handleChatClick = async (app, isReceived) => {
    if (!onNavigateToChat) return;
    setChatLoading(app.id);

    let { data: room, error: fetchErr } = await supabase
      .from("chat_rooms").select("*").eq("match_id", app.id).maybeSingle();

    if (!room) {
      // 채팅방 조회 실패 시 새로 생성 시도
      const guestName  = isReceived ? app.guestTeam?.team_name  : activeTeam.team_name;
      const targetName = isReceived ? activeTeam.team_name      : app.targetTeam?.team_name;
      const sportType  = isReceived ? app.guestTeam?.sports_type : app.targetTeam?.sports_type;
      const { data: newRoom, error: insertErr } = await supabase
        .from("chat_rooms")
        .insert([{ match_id: app.id, team1_name: guestName || "팀1", team2_name: targetName || "팀2", sports_type: sportType || null }])
        .select().single();
      if (insertErr) { alert("채팅방을 열 수 없어요.\n" + insertErr.message); setChatLoading(null); return; }
      room = newRoom;
    }

    setChatLoading(null);
    if (room) onNavigateToChat(room);
  };

  const pendingCount = receivedGroups.flatMap((g) => g.apps).filter((a) => a.status === "pending").length;

  // 탭별 display 필터 — 원본 state는 건드리지 않고 렌더링 시점에만 분리
  // 받은 신청 탭: accepted 제외 (accepted는 수락된 매칭 탭에서만 표시)
  const receivedGroupsForDisplay = receivedGroups
    .map((g) => ({ ...g, apps: g.apps.filter((a) => a.status !== "accepted") }))
    .filter((g) => g.apps.length > 0);
  // 보낸 신청 탭: accepted 제외
  const sentForDisplay = sent.filter((a) => a.status !== "accepted");

  const acceptedMatchList = [
    ...receivedGroups.flatMap((g) => g.apps)
      .filter((a) => a.status === "accepted")
      .map((a) => ({ ...a, isHost: true, opponentTeam: a.guestTeam, opponentTeamId: a.guest_team_id })),
    ...sent
      .filter((a) => a.status === "accepted")
      .map((a) => ({ ...a, isHost: false, opponentTeam: a.targetTeam, opponentTeamId: a.host_team_id })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullY(Math.min(diff * 0.4, 80));
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (pullY > 50) {
      setRefreshing(true);
      await fetchApplications();
      setRefreshing(false);
    }
    setPullY(0);
    touchStartY.current = 0;
  }, [pullY, fetchApplications]);

  return (
    <div ref={containerRef} style={{ ...styles.container, overflowY: "auto" }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {(pullY > 0 || refreshing) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: refreshing ? 40 : pullY, overflow: "hidden", transition: refreshing ? "height 0.2s" : "none", background: "#f7f8fa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...styles.spinner, width: 20, height: 20, borderWidth: 2 }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{refreshing ? "새로고침 중..." : pullY > 50 ? "놓으면 새로고침" : "당겨서 새로고침"}</span>
          </div>
        </div>
      )}
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.logoRow}>
          <div style={{ width: 48 }} />
          <div style={styles.logoCenterWrap}>
            <span style={styles.logoText}>크루픽</span>
            <span style={styles.logoSub}>title</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <NotificationBell />
          </div>
        </div>
        <div style={styles.headerTitle}>내 매칭</div>
        <div style={styles.headerSub}>신청 현황을 확인하고 수락/거절하세요</div>
      </div>

      {!activeTeam && (
        <div style={styles.emptyWrap}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>📋</div>
          <div style={{ fontSize: 16, color: "#6b7280", fontWeight: 600 }}>활성 팀을 설정해주세요</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 6, textAlign: "center", lineHeight: 1.6 }}>
            팀 관리 탭에서 팀을 선택하면<br />매칭 신청 현황을 확인할 수 있어요
          </div>
        </div>
      )}

      {activeTeam && loading && (
        <div style={styles.emptyWrap}>
          <div style={styles.spinner} />
          <p style={{ color: "#9ca3af", fontSize: 14, marginTop: 16 }}>불러오는 중...</p>
        </div>
      )}

      {error && (
        <div style={styles.errorBox}>
          <span>⚠️</span>
          <span style={{ marginLeft: 8, fontSize: 13, color: "#dc2626" }}>{error}</span>
        </div>
      )}

      {activeTeam && !loading && (
        <>
          <div style={styles.teamNameRow}>
            <span style={styles.myTeamBadge}>🏅 {activeTeam.team_name}</span>
          </div>

          {/* 탭 */}
          <div style={styles.tabRow}>
            <button
              style={{ ...styles.tab, borderBottom: activeTab === "accepted" ? "2px solid #E24B4A" : "2px solid transparent", color: activeTab === "accepted" ? "#E24B4A" : "#9ca3af" }}
              onClick={() => setActiveTab("accepted")}
            >
              수락된 매칭
              {acceptedMatchList.length > 0 && (
                <span style={{ ...styles.badge, background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}>
                  {acceptedMatchList.length}
                </span>
              )}
            </button>
            <button
              style={{ ...styles.tab, borderBottom: activeTab === "received" ? "2px solid #E24B4A" : "2px solid transparent", color: activeTab === "received" ? "#E24B4A" : "#9ca3af" }}
              onClick={() => setActiveTab("received")}
            >
              받은 신청
              {pendingCount > 0 && <span style={styles.badge}>{pendingCount}</span>}
            </button>
            <button
              style={{ ...styles.tab, borderBottom: activeTab === "sent" ? "2px solid #E24B4A" : "2px solid transparent", color: activeTab === "sent" ? "#E24B4A" : "#9ca3af" }}
              onClick={() => setActiveTab("sent")}
            >
              보낸 신청
            </button>
            <button
              style={{ ...styles.tab, borderBottom: activeTab === "club" ? "2px solid #E24B4A" : "2px solid transparent", color: activeTab === "club" ? "#E24B4A" : "#9ca3af" }}
              onClick={() => { setActiveTab("club"); fetchClubApps(); }}
            >
              회원 신청
              {clubApps.filter((a) => a.status === "pending").length > 0 && (
                <span style={styles.badge}>{clubApps.filter((a) => a.status === "pending").length}</span>
              )}
            </button>
          </div>

          {/* ── 받은 신청 탭 ── */}
          {activeTab === "received" && (
            <div style={styles.listWrap}>
              {receivedGroupsForDisplay.length === 0 ? (
                <EmptyState icon="📭" text="받은 매칭 신청이 없어요" sub="다른 팀이 신청하면 여기에 표시됩니다" />
              ) : (
                receivedGroupsForDisplay.map((group) => (
                  <ReceivedGroup
                    key={group.postId || group.apps[0]?.id}
                    group={group}
                    activeTeam={activeTeam}
                    onAccept={(id) => handleStatusUpdate(id, "accepted")}
                    onReject={(id) => handleStatusUpdate(id, "rejected")}
                    onCancel={(id) => handleCancel(id, true)}
                    onDelete={(id) => handleDelete(id, true, "신청을 삭제하시겠어요?", "삭제 후 복구는 불가합니다.")}
                    onDeleteRecord={(id) => handleDelete(id, true, "이 매칭 기록을 삭제하시겠어요?", "삭제 후 복구는 불가합니다.")}
                    onChatClick={(app) => handleChatClick(app, true)}
                    actionLoading={actionLoading}
                    chatLoading={chatLoading}
                    resultMap={resultMap}
                    reviewedSet={reviewedSet}
                    scheduleMap={scheduleMap}
                    onResultClick={(app) => setResultTarget({ app, isReceived: true })}
                    onReviewClick={(app) => setReviewTarget({
                      app,
                      opponentTeamId:   app.guest_team_id,
                      opponentTeamName: app.guestTeam?.team_name || "상대 팀",
                      result:           resultMap[app.id] || null,
                    })}
                  />
                ))
              )}
            </div>
          )}

          {/* ── 보낸 신청 탭 ── */}
          {activeTab === "sent" && (
            <div style={styles.listWrap}>
              {sentForDisplay.length === 0 && clubSentApps.length === 0 ? (
                <EmptyState icon="📤" text="보낸 신청이 없어요" sub="'매칭 찾기'에서 팀이나 모집글을 찾아 신청해보세요" />
              ) : (
                <>
                  {sentForDisplay.map((app) => (
                    <SentCard
                      key={app.id}
                      app={app}
                      onChatClick={() => handleChatClick(app, false)}
                      onCancel={() => handleCancel(app.id, false)}
                      onDelete={() => handleDelete(app.id, false, "신청을 삭제하시겠어요?", "삭제 후 복구는 불가합니다.")}
                      onDeleteRecord={() => handleDelete(app.id, false, "이 매칭 기록을 삭제하시겠어요?", "삭제 후 복구는 불가합니다.")}
                      chatLoading={chatLoading === app.id}
                      actionLoading={actionLoading === app.id}
                      resultData={resultMap[app.id] || null}
                      hasReviewed={reviewedSet.has(app.id)}
                      scheduleData={scheduleMap[app.id] || null}
                      onResultClick={() => setResultTarget({ app, isReceived: false })}
                      onReviewClick={() => setReviewTarget({
                        app,
                        opponentTeamId:   app.host_team_id,
                        opponentTeamName: app.targetTeam?.team_name || "상대 팀",
                        result:           resultMap[app.id] || null,
                      })}
                    />
                  ))}

                  {/* 보낸 회원 신청 */}
                  {clubSentApps.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", marginTop: 16, marginBottom: 8 }}>회원 가입 신청</div>
                      {clubSentApps.map((app) => {
                        const stl = STATUS_LABEL[app.status] || STATUS_LABEL.pending;
                        const isAccepted = app.status === "accepted";
                        const isLoading = clubActionLoading === app.id;
                        return (
                          <div key={`club-sent-${app.id}`} style={styles.appCard}>
                            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>📋 {app.recruitment_title || "회원 모집글"}</div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{app.recruitment_team_name || "팀"}</span>
                              <span style={{ padding: "3px 10px", background: stl.bg, color: stl.color, border: `1px solid ${stl.border}`, borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{stl.text}</span>
                            </div>
                            {app.message && (
                              <div style={{ fontSize: 12, color: "#6b7280", background: "#f9fafb", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>💬 {app.message}</div>
                            )}
                            {isAccepted && (
                              <button
                                style={{ width: "100%", padding: "10px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: isLoading ? 0.6 : 1 }}
                                onClick={() => handleClubChat(app)}
                                disabled={isLoading}
                              >
                                {isLoading ? "이동 중..." : "💬 채팅하기"}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── 수락된 매칭 탭 ── */}
          {activeTab === "accepted" && (
            <div style={styles.listWrap}>
              {acceptedMatchList.length === 0 ? (
                <EmptyState icon="🤝" text="수락된 매칭이 없어요" sub="매칭이 수락되면 여기에 표시됩니다" />
              ) : (
                acceptedMatchList.map((app) => (
                  <AcceptedMatchCard
                    key={app.id + (app.isHost ? "-h" : "-g")}
                    app={app}
                    isHost={app.isHost}
                    opponentTeam={app.opponentTeam}
                    scheduleData={scheduleMap[app.id] || null}
                    resultData={resultMap[app.id] || null}
                    hasReviewed={reviewedSet.has(app.id)}
                    chatLoading={chatLoading === app.id}
                    actionLoading={actionLoading === app.id}
                    onChatClick={() => handleChatClick(app, app.isHost)}
                    onCancel={() => handleCancel(app.id, app.isHost)}
                    onDelete={() => handleDelete(
                      app.id, app.isHost,
                      "이 매칭 기록을 삭제하시겠어요?",
                      "삭제 후 복구는 불가합니다."
                    )}
                    onResultClick={() => setResultTarget({ app, isReceived: app.isHost })}
                    onReviewClick={() => setReviewTarget({
                      app,
                      opponentTeamId:   app.opponentTeamId,
                      opponentTeamName: app.opponentTeam?.team_name || "상대 팀",
                      result:           resultMap[app.id] || null,
                    })}
                  />
                ))
              )}
            </div>
          )}

          {/* ── 회원 신청 탭 ── */}
          {activeTab === "club" && (
            <div style={styles.listWrap}>
              {/* 팀원 가입 신청 (team_members) */}
              {memberApps.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", marginBottom: 8 }}>팀원 가입 신청</div>
                  {memberApps.map((m) => {
                    const nick = m.resolvedNick || "(닉네임 없음)";
                    const busy = memberActionLoading === m.id;
                    return (
                      <div key={`tm-${m.id}`} style={styles.appCard}>
                        {m.team_name && (
                          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>🏅 {m.team_name}</div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{nick}</span>
                          <span style={{ padding: "3px 10px", background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>대기중</span>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            style={{ flex: 1, padding: "10px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: busy ? 0.6 : 1 }}
                            onClick={() => handleMemberAccept(m.id)}
                            disabled={busy}
                          >
                            {busy ? "처리 중..." : "수락"}
                          </button>
                          <button
                            style={{ flex: 1, padding: "10px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: busy ? 0.6 : 1 }}
                            onClick={() => handleMemberReject(m.id)}
                            disabled={busy}
                          >
                            거절
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* 회원 모집 신청 */}
              {clubLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                  <div style={styles.spinner} />
                </div>
              ) : clubApps.length === 0 && memberApps.length === 0 ? (
                <EmptyState icon="📋" text="회원 신청이 없어요" sub="회원 모집글에 신청이 오면 여기에 표시됩니다" />
              ) : (
                clubApps.map((app) => {
                  const st = STATUS_LABEL[app.status] || STATUS_LABEL.pending;
                  const isPending = app.status === "pending";
                  const isLoading = clubActionLoading === app.id;
                  return (
                    <div key={app.id} style={styles.appCard}>
                      {app.recruitment_title && (
                        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>📋 {app.recruitment_title}</div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{app.applicant_name || "신청자"}</span>
                        <span style={{ padding: "3px 10px", background: st.bg, color: st.color, border: `1px solid ${st.border}`, borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{st.text}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                        {app.age && <span style={{ fontSize: 12, color: "#6b7280", background: "#f5f6fa", padding: "3px 10px", borderRadius: 999, border: "1px solid #edf0f3" }}>🎂 {app.age}세</span>}
                        {app.gender && <span style={{ fontSize: 12, color: "#6b7280", background: "#f5f6fa", padding: "3px 10px", borderRadius: 999, border: "1px solid #edf0f3" }}>{app.gender === "남성" ? "👨" : "👩"} {app.gender}</span>}
                        {app.career && <span style={{ fontSize: 12, color: "#6b7280", background: "#f5f6fa", padding: "3px 10px", borderRadius: 999, border: "1px solid #edf0f3" }}>⏱ {app.career}</span>}
                      </div>
                      {app.message && (
                        <div style={{ fontSize: 13, color: "#6b7280", background: "#f9fafb", borderRadius: 8, padding: "10px 12px", marginBottom: 10, lineHeight: 1.6 }}>
                          💬 {app.message}
                        </div>
                      )}
                      {app.applicant_email && <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>📧 {app.applicant_email}</div>}
                      {isPending && (
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            style={{ flex: 1, padding: "10px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: isLoading ? 0.6 : 1 }}
                            onClick={() => handleClubAccept(app)}
                            disabled={isLoading}
                          >
                            {isLoading ? "처리 중..." : "수락"}
                          </button>
                          <button
                            style={{ flex: 1, padding: "10px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: isLoading ? 0.6 : 1 }}
                            onClick={() => handleClubReject(app.id)}
                            disabled={isLoading}
                          >
                            거절
                          </button>
                        </div>
                      )}
                      {app.status === "accepted" && (
                        <button
                          style={{ width: "100%", padding: "10px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: isLoading ? 0.6 : 1 }}
                          onClick={() => handleClubChat(app)}
                          disabled={isLoading}
                        >
                          {isLoading ? "이동 중..." : "💬 채팅하기"}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}

      {/* 취소 확인 모달 */}
      {confirmCancel && (
        <ConfirmCancelModal
          onConfirm={doCancel}
          onClose={() => setConfirmCancel(null)}
        />
      )}

      {/* 삭제 확인 모달 */}
      {confirmDelete && (
        <ConfirmDeleteModal
          title={confirmDelete.title}
          body={confirmDelete.body}
          onConfirm={doDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {/* 경기 결과 입력/확인 오버레이 */}
      {resultTarget && (
        <MatchResult
          key={resultRefreshKey}
          app={resultTarget.app}
          myTeam={activeTeam}
          hostTeamName={
            resultTarget.isReceived
              ? activeTeam.team_name
              : (resultTarget.app.targetTeam?.team_name || "호스트 팀")
          }
          guestTeamName={
            resultTarget.isReceived
              ? (resultTarget.app.guestTeam?.team_name || "게스트 팀")
              : activeTeam.team_name
          }
          sportsType={activeTeam.sports_type}
          onClose={() => setResultTarget(null)}
          onDone={() => { setResultTarget(null); fetchApplications(); }}
        />
      )}

      {/* 매너 평가 오버레이 */}
      {reviewTarget && (
        <MannerReview
          app={reviewTarget.app}
          myTeam={activeTeam}
          opponentTeamId={reviewTarget.opponentTeamId}
          opponentTeamName={reviewTarget.opponentTeamName}
          confirmedAt={reviewTarget?.result?.confirmed_at}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => { setReviewTarget(null); fetchApplications(); }}
        />
      )}
    </div>
  );
}

// ── 받은 신청 그룹 ──
function ReceivedGroup({ group, activeTeam, onAccept, onReject, onCancel, onDelete, onDeleteRecord, onChatClick, actionLoading, chatLoading, resultMap, reviewedSet, scheduleMap, onResultClick, onReviewClick }) {
  // 그룹 내 수락된 앱 확인 (자동 거절 판단용)
  const acceptedApp      = group.apps.find((a) => a.status === "accepted");
  const acceptedAt       = acceptedApp ? new Date(acceptedApp.created_at).getTime() : null;
  const autoRejectActive = acceptedAt && (Date.now() - acceptedAt > H24);

  // 모집글 기반 그룹인 경우 헤더 표시
  const showPostHeader = group.type === "post" && group.post;

  return (
    <div style={styles.groupWrap}>
      {/* 모집글 정보 헤더 */}
      {showPostHeader && (
        <div style={styles.postHeader}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#E24B4A", marginBottom: 4 }}>
            📋 모집글 신청 · {group.apps.length}팀 신청
          </div>
          {group.post.game_date && (
            <div style={{ fontSize: 12, color: "#374151" }}>
              📅 {formatGameDate(group.post.game_date)}
              {buildLocation(group.post) && <span style={{ marginLeft: 6, color: "#6b7280" }}>📍 {buildLocation(group.post)}</span>}
            </div>
          )}
          {acceptedApp && (
            <div style={{ fontSize: 11, color: "#16a34a", marginTop: 4 }}>
              ✅ {acceptedApp.guestTeam?.team_name || "상대 팀"} 수락 완료
            </div>
          )}
        </div>
      )}

      {/* 각 신청 카드 */}
      {group.apps.map((app) => {
        const effectiveStatus =
          app.status === "pending" && autoRejectActive ? "auto_rejected" : app.status;

        return (
          <ReceivedCard
            key={app.id}
            app={{ ...app, status: effectiveStatus }}
            originalStatus={app.status}
            post={group.post}
            hasAcceptedSibling={!!acceptedApp && app.id !== acceptedApp?.id}
            onAccept={() => onAccept(app.id)}
            onReject={() => onReject(app.id)}
            onCancel={() => onCancel(app.id)}
            onDelete={() => onDelete(app.id)}
            onDeleteRecord={() => onDeleteRecord(app.id)}
            onChatClick={() => onChatClick(app)}
            actionLoading={actionLoading === app.id}
            chatLoading={chatLoading === app.id}
            resultData={resultMap?.[app.id] || null}
            hasReviewed={reviewedSet?.has(app.id) || false}
            scheduleData={scheduleMap?.[app.id] || null}
            onResultClick={() => onResultClick(app)}
            onReviewClick={() => onReviewClick(app)}
          />
        );
      })}

      {/* 수락 후 안내 문구 */}
      {group.type === "post" && acceptedApp && !autoRejectActive && (
        <div style={styles.autoRejectNotice}>
          💡 한 팀 수락 후 나머지 신청팀은 직접 거절하거나, 24시간 후 자동 거절됩니다.
        </div>
      )}
    </div>
  );
}

// ── 받은 신청 카드 ──
function ReceivedCard({ app, originalStatus, post, hasAcceptedSibling, onAccept, onReject, onCancel, onDelete, onDeleteRecord, onChatClick, actionLoading, chatLoading, resultData, hasReviewed, scheduleData, onResultClick, onReviewClick }) {
  const guestTeam = app.guestTeam;
  const sport     = SPORTS[guestTeam?.sports_type] || { icon: "🏅", label: guestTeam?.sports_type || "-" };
  const statusInfo = STATUS_LABEL[app.status] || STATUS_LABEL.pending;
  const createdAt  = app.created_at
    ? new Date(app.created_at).toLocaleDateString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

  const [showTeamModal, setShowTeamModal] = useState(false);
  // 경기 결과 confirmed + 매너 평가 완료 → 삭제하기 버튼으로 전환
  const isFullyDone = resultData?.result_status === "confirmed" && hasReviewed;

  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div
          style={{ ...styles.sportIcon, cursor: app.guest_team_id ? "pointer" : "default" }}
          onClick={() => app.guest_team_id && setShowTeamModal(true)}
        >
          {sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{ ...styles.cardTeamName, cursor: app.guest_team_id ? "pointer" : "default", textDecoration: app.guest_team_id ? "underline" : "none", textDecorationColor: "#d1d5db" }}
            onClick={() => app.guest_team_id && setShowTeamModal(true)}
          >
            {guestTeam?.team_name || "알 수 없는 팀"}
          </div>
          {app.guest_team_id && (
            <div style={{ fontSize: 11, color: "#b0b8c1", marginBottom: 2 }}>👆 팀 이름을 눌러 정보 확인</div>
          )}
          <div style={styles.cardSub}>{sport.label} · {createdAt}</div>
        </div>
        <div style={{ ...styles.statusBadge, color: statusInfo.color, background: statusInfo.bg, border: `1px solid ${statusInfo.border}` }}>
          {statusInfo.text}
        </div>
      </div>

      {app.message && (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#f5f6fa", borderRadius: 8, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
          💬 {app.message}
        </div>
      )}

      {/* 수락된 매칭: 일정 표시 (schedules 우선, 없으면 post) */}
      {app.status === "accepted" && scheduleData?.game_date && (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", fontSize: 12 }}>
          <div style={{ color: "#16a34a", fontWeight: 700, marginBottom: 2 }}>📅 {formatGameDate(scheduleData.game_date)}{scheduleData.game_time && ` ${scheduleData.game_time}`}</div>
          {scheduleData.location && <div style={{ color: "#374151" }}>📍 {scheduleData.location}</div>}
        </div>
      )}
      {app.status === "accepted" && !scheduleData && post && (post.game_date || buildLocation(post)) && (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", fontSize: 12 }}>
          {post.game_date && <div style={{ color: "#16a34a", fontWeight: 700, marginBottom: 2 }}>📅 {formatGameDate(post.game_date)}</div>}
          {buildLocation(post) && <div style={{ color: "#374151" }}>📍 {buildLocation(post)}</div>}
        </div>
      )}

      {/* 버튼 영역 */}
      {originalStatus === "pending" && app.status === "pending" && !hasAcceptedSibling && (
        <>
          <div style={styles.actionRow}>
            <button style={{ ...styles.rejectBtn, opacity: actionLoading ? 0.5 : 1 }} onClick={onReject} disabled={actionLoading}>거절</button>
            <button style={{ ...styles.acceptBtn, opacity: actionLoading ? 0.5 : 1 }} onClick={onAccept} disabled={actionLoading}>
              {actionLoading ? "처리 중..." : "✅ 수락"}
            </button>
          </div>
          <div style={styles.autoRejectHint}>한 팀 수락 후 나머지 신청팀은 직접 거절하거나, 24시간 후 자동 거절됩니다.</div>
        </>
      )}
      {/* 수락됐지만 아직 다른 pending 있을 때도 거절 버튼 표시 */}
      {originalStatus === "pending" && app.status === "pending" && hasAcceptedSibling && (
        <div style={styles.actionRow}>
          <button style={{ ...styles.rejectBtn, flex: 1, opacity: actionLoading ? 0.5 : 1 }} onClick={onReject} disabled={actionLoading}>거절하기</button>
        </div>
      )}
      {app.status === "accepted" && (
        <div>
          <div style={styles.acceptedMsg}>🎉 매칭이 성사됐어요!</div>
          <button style={{ ...styles.chatBtn, opacity: chatLoading ? 0.6 : 1 }} onClick={onChatClick} disabled={chatLoading}>
            {chatLoading ? "채팅방 이동 중..." : "💬 채팅하기"}
          </button>

          {/* 경기 결과 버튼 (호스트 전용) */}
          <ResultButtons
            resultData={resultData}
            hasReviewed={hasReviewed}
            isHost={true}
            onResultClick={onResultClick}
            onReviewClick={onReviewClick}
          />

          {isFullyDone ? (
            <button style={{ ...styles.deleteBtn, width: "100%", marginTop: 8 }} onClick={onDeleteRecord}>
              🗑 삭제하기
            </button>
          ) : (
            <button
              style={{ ...styles.cancelBtn, width: "100%", marginTop: 8, opacity: actionLoading ? 0.5 : 1 }}
              onClick={onCancel}
              disabled={actionLoading}
            >
              취소하기
            </button>
          )}
        </div>
      )}
      {(app.status === "rejected" || app.status === "cancelled" || app.status === "auto_rejected") && (
        <div style={styles.rejectedMsg}>
          {app.status === "rejected"      && "이 신청을 거절했어요"}
          {app.status === "cancelled"     && "취소된 매칭이에요"}
          {app.status === "auto_rejected" && "수락 후 24시간이 지나 자동 거절됐어요"}
        </div>
      )}

      {/* 모든 카드 하단 공통 삭제 버튼 */}
      <button style={{ ...styles.deleteBtn, marginTop: 10 }} onClick={onDelete}>🗑 삭제</button>

      {showTeamModal && (
        <TeamDetailModal teamId={app.guest_team_id} onClose={() => setShowTeamModal(false)} />
      )}
    </div>
  );
}

// ── 보낸 신청 카드 ──
function SentCard({ app, onChatClick, onCancel, onDelete, onDeleteRecord, chatLoading, actionLoading, resultData, hasReviewed, scheduleData, onResultClick, onReviewClick }) {
  const targetTeam = app.targetTeam;
  const sport      = SPORTS[targetTeam?.sports_type] || { icon: "🏅", label: targetTeam?.sports_type || "-" };
  const status     = STATUS_LABEL[app.status] || STATUS_LABEL.pending;
  const createdAt  = app.created_at
    ? new Date(app.created_at).toLocaleDateString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

  const postGameDate = app.post ? formatGameDate(app.post.game_date) : null;
  const postLocation = app.post ? buildLocation(app.post) : null;
  // 경기 결과 confirmed + 매너 평가 완료 → 삭제하기 버튼으로 전환
  const isFullyDone = resultData?.result_status === "confirmed" && hasReviewed;

  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div style={styles.sportIcon}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />}</div>
        <div style={{ flex: 1 }}>
          <div style={styles.cardTeamName}>{targetTeam?.team_name || "알 수 없는 팀"}</div>
          <div style={styles.cardSub}>
            {sport.label} · {createdAt}
            {app.post_id && <span style={{ color: "#E24B4A", marginLeft: 6, fontWeight: 600 }}>모집글</span>}
          </div>
        </div>
        <div style={{ ...styles.statusBadge, color: status.color, background: status.bg, border: `1px solid ${status.border}` }}>
          {status.text}
        </div>
      </div>

      {app.message && (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#f5f6fa", borderRadius: 8, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
          💬 {app.message}
        </div>
      )}

      {/* 수락된 매칭: 일정 표시 (schedules 우선) */}
      {app.status === "accepted" && scheduleData?.game_date && (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", fontSize: 12 }}>
          <div style={{ color: "#16a34a", fontWeight: 700, marginBottom: 2 }}>📅 {formatGameDate(scheduleData.game_date)}{scheduleData.game_time && ` ${scheduleData.game_time}`}</div>
          {scheduleData.location && <div style={{ color: "#374151" }}>📍 {scheduleData.location}</div>}
        </div>
      )}

      {/* 모집글 일정 정보 */}
      {app.status !== "accepted" && (postGameDate || postLocation) && (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#fff8f8", borderRadius: 8, border: "1px solid #fecaca", fontSize: 12 }}>
          {postGameDate && <div style={{ color: "#E24B4A", fontWeight: 700, marginBottom: 2 }}>📅 {postGameDate}</div>}
          {postLocation && <div style={{ color: "#374151" }}>📍 {postLocation}</div>}
        </div>
      )}

      {app.status === "pending" && (
        <>
          <div style={styles.pendingMsg}>⏳ 상대 팀의 응답을 기다리는 중이에요</div>
          <button
            style={{ ...styles.cancelBtn, width: "100%", marginTop: 8, opacity: actionLoading ? 0.5 : 1 }}
            onClick={onCancel}
            disabled={actionLoading}
          >
            신청 취소하기
          </button>
        </>
      )}
      {app.status === "accepted" && (
        <div>
          <div style={styles.acceptedMsg}>🎉 매칭이 성사됐어요!</div>
          <button style={{ ...styles.chatBtn, opacity: chatLoading ? 0.6 : 1 }} onClick={onChatClick} disabled={chatLoading}>
            {chatLoading ? "채팅방 이동 중..." : "💬 채팅하기"}
          </button>

          {/* 경기 결과 버튼 (게스트) */}
          <ResultButtons
            resultData={resultData}
            hasReviewed={hasReviewed}
            isHost={false}
            onResultClick={onResultClick}
            onReviewClick={onReviewClick}
          />

          {isFullyDone ? (
            <button style={{ ...styles.deleteBtn, width: "100%", marginTop: 8 }} onClick={onDeleteRecord}>
              🗑 삭제하기
            </button>
          ) : (
            <button
              style={{ ...styles.cancelBtn, width: "100%", marginTop: 8, opacity: actionLoading ? 0.5 : 1 }}
              onClick={onCancel}
              disabled={actionLoading}
            >
              취소하기
            </button>
          )}
        </div>
      )}
      {(app.status === "rejected" || app.status === "cancelled") && (
        <div style={styles.rejectedMsg}>
          {app.status === "rejected"  && "아쉽지만 거절됐어요. 다른 팀에 신청해보세요"}
          {app.status === "cancelled" && "취소된 매칭이에요"}
        </div>
      )}

      {/* 모든 카드 하단 공통 삭제 버튼 */}
      <button style={{ ...styles.deleteBtn, marginTop: 10 }} onClick={onDelete}>🗑 삭제</button>

    </div>
  );
}

// ── 수락된 매칭 카드 (3번 탭 전용) ──
function AcceptedMatchCard({ app, isHost, opponentTeam, scheduleData, resultData, hasReviewed, chatLoading, actionLoading, onChatClick, onCancel, onDelete, onResultClick, onReviewClick }) {
  const sport = SPORTS[opponentTeam?.sports_type] || { icon: "🏅", label: opponentTeam?.sports_type || "-" };
  const [showTeamModal, setShowTeamModal] = useState(false);
  const opponentTeamId = isHost ? app.guest_team_id : app.host_team_id;

  // 경기 결과 confirmed + 매너 평가 완료 → 삭제하기 버튼으로 전환
  const isFullyDone = resultData?.result_status === "confirmed" && hasReviewed;

  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div style={{ ...styles.sportIcon, cursor: "pointer" }} onClick={() => setShowTeamModal(true)}>
          {opponentTeam?.logo_url
            ? <img src={opponentTeam.logo_url} alt="로고" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : (sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...styles.cardTeamName, cursor: "pointer", textDecoration: "underline", textDecorationColor: "#d1d5db" }} onClick={() => setShowTeamModal(true)}>
            {opponentTeam?.team_name || "상대 팀"}
          </div>
          <div style={styles.cardSub}>{sport.label} · {isHost ? "내가 호스트" : "내가 게스트"}</div>
        </div>
        <div style={{ ...styles.statusBadge, color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
          수락됨
        </div>
      </div>

      {/* 일정 정보 */}
      {scheduleData?.game_date ? (
        <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", fontSize: 12 }}>
          <div style={{ color: "#16a34a", fontWeight: 700, marginBottom: 2 }}>📅 {formatGameDate(scheduleData.game_date)}{scheduleData.game_time && ` ${scheduleData.game_time}`}</div>
          {scheduleData.location && <div style={{ color: "#374151" }}>📍 {scheduleData.location}</div>}
        </div>
      ) : (
        <div style={{ margin: "10px 0 0", padding: "8px 12px", background: "#f9fafb", borderRadius: 8, border: "1px solid #edf0f3", fontSize: 11, color: "#9ca3af" }}>
          📅 아직 일정이 등록되지 않았어요 (채팅에서 등록 가능)
        </div>
      )}

      <div style={styles.acceptedMsg}>🎉 매칭이 성사됐어요!</div>
      <button style={{ ...styles.chatBtn, opacity: chatLoading ? 0.6 : 1 }} onClick={onChatClick} disabled={chatLoading}>
        {chatLoading ? "채팅방 이동 중..." : "💬 채팅하기"}
      </button>

      <ResultButtons
        resultData={resultData}
        hasReviewed={hasReviewed}
        isHost={isHost}
        onResultClick={onResultClick}
        onReviewClick={onReviewClick}
      />

      {isFullyDone ? (
        /* 경기 완료 + 매너 평가 완료 → 삭제하기 버튼 */
        <button style={{ ...styles.deleteBtn, width: "100%", marginTop: 8 }} onClick={onDelete}>
          🗑 삭제하기
        </button>
      ) : (
        /* 아직 진행 중 → 취소하기 버튼 */
        <button
          style={{ ...styles.cancelBtn, width: "100%", marginTop: 8, opacity: actionLoading ? 0.5 : 1 }}
          onClick={onCancel}
          disabled={actionLoading}
        >
          취소하기
        </button>
      )}

      {showTeamModal && opponentTeamId && (
        <TeamDetailModal teamId={opponentTeamId} onClose={() => setShowTeamModal(false)} />
      )}

    </div>
  );
}

// ── 결과/매너 버튼 공용 컴포넌트 ──
function ResultButtons({ resultData, hasReviewed, isHost, onResultClick, onReviewClick }) {
  const st = resultData?.result_status;

  // 결과 없음 → 양쪽 모두 입력 가능
  if (!resultData) {
    return (
      <button style={styles.resultBtn} onClick={onResultClick}>
        📊 결과 입력하기
      </button>
    );
  }

  // pending → host_confirmed / guest_confirmed 기반 분기
  if (st === "pending") {
    const myConfirmed  = isHost ? (resultData.host_confirmed  ?? false) : (resultData.guest_confirmed ?? false);
    const oppConfirmed = isHost ? (resultData.guest_confirmed ?? false) : (resultData.host_confirmed  ?? false);

    if (myConfirmed && !oppConfirmed) {
      return <div style={styles.resultWaitMsg}>⏳ 상대 팀이 결과 확인 중</div>;
    }
    return (
      <button style={{ ...styles.resultBtn, background: "#1d4ed8" }} onClick={onResultClick}>
        📋 결과 확인하기
      </button>
    );
  }

  // confirmed → 결과 보기 + 매너 평가
  if (st === "confirmed") {
    const hs = resultData.host_score;
    const gs = resultData.guest_score;
    const myWin  = isHost ? hs > gs : gs > hs;
    const isDraw = hs === gs;
    return (
      <div style={{ marginTop: 10 }}>
        <div style={styles.confirmedResultBox}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
            {hs} : {gs}
          </span>
          <span style={{
            marginLeft: 10, fontSize: 12, fontWeight: 700,
            color: isDraw ? "#d97706" : myWin ? "#16a34a" : "#E24B4A",
          }}>
            {isDraw ? "🤝 무승부" : myWin ? "🏆 승리" : "💔 패배"}
          </span>
          <button style={styles.viewResultBtn} onClick={onResultClick}>상세보기</button>
        </div>
        {!hasReviewed && (
          <button style={styles.mannerBtn} onClick={onReviewClick}>
            ⭐ 매너 평가하기
          </button>
        )}
        {hasReviewed && (
          <div style={styles.reviewDoneMsg}>✅ 매너 평가 완료</div>
        )}
      </div>
    );
  }
  return null;
}

// ── 팀 상세 정보 모달 ──
function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function TeamDetailModal({ teamId, onClose }) {
  const [team, setTeam]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewerSrc, setViewerSrc] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("teams").select("*").eq("id", teamId).maybeSingle();
      setTeam(data);
      setLoading(false);
    })();
  }, [teamId]);

  const sport        = team ? (SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type }) : { icon: "🏅", label: "" };
  const totalMembers = team ? (team.male_count || 0) + (team.female_count || 0) : 0;

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.teamModalBox} onClick={(e) => e.stopPropagation()}>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0" }}>
            <div style={styles.spinner} />
            <p style={{ color: "#9ca3af", fontSize: 13, marginTop: 14 }}>팀 정보 불러오는 중...</p>
          </div>
        ) : !team ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 14 }}>
            팀 정보를 불러올 수 없어요
          </div>
        ) : (
          <>
            {/* 히어로 영역 */}
            <div style={{ textAlign: "center", marginBottom: 14 }}>
              {team.logo_url ? (
                <img src={team.logo_url} alt="팀 로고" loading="lazy" style={{ width: 64, height: 64, borderRadius: 14, objectFit: "cover", border: "2px solid #e5e7eb", marginBottom: 8, cursor: "pointer" }} onClick={() => setViewerSrc(team.logo_url)} />
              ) : (
                <div style={{ fontSize: 38, marginBottom: 6 }}>
                  {sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 38, height: 38, objectFit: "contain" }} />}
                </div>
              )}
              <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{team.team_name}</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>{sport.label}</div>
              <div style={{ display: "flex", gap: 5, justifyContent: "center", flexWrap: "wrap", marginBottom: 8 }}>
                {team.level && <SportLevelBadge level={team.level} />}
                {team.skill_level && (
                  <span style={{ padding: "3px 10px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0369a1" }}>
                    {team.skill_level}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <LevelBadge level={team.match_level} />
                <MannerBadge score={team.manner_avg} />
              </div>
              {team.region && (
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>📍 {team.region}</div>
              )}
            </div>

            {/* 단체사진 */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>📸 팀 단체사진</div>
              {team.team_photo ? (
                <img src={team.team_photo} alt="단체사진" loading="lazy" style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb", cursor: "pointer", display: "block" }} onClick={() => setViewerSrc(team.team_photo)} />
              ) : (
                <div style={{ width: "100%", height: 90, background: "#f3f4f6", borderRadius: 10, border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#9ca3af" }}>
                  📷 등록된 사진이 없습니다
                </div>
              )}
            </div>

            {/* 팀 정보 */}
            <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "2px 12px", marginBottom: 10 }}>
              <TeamInfoRow label="구력 레벨"     value={formatSportLevel(team.sports_type, team.level)} />
              <TeamInfoRow label="실력"           value={team.skill_level || "미입력"} />
              <TeamInfoRow label="전적 레벨"      value={formatRecordLevel(team)} />
              <TeamInfoRow label="전적"           value={formatRecord(team)} />
              <TeamInfoRow label="매너 점수"      value={formatManner(team)} />
              <TeamInfoRow label="총 인원"        value={totalMembers > 0 ? `${totalMembers}명 (남 ${team.male_count || 0} / 여 ${team.female_count || 0})` : "미입력"} />
              <TeamInfoRow label="연령대"         value={parseAgeGroup(team.age_group) || "미입력"} />
              <TeamInfoRow label="평균 구력"      value={team.member_avg_career || "미입력"} />
              <TeamInfoRow label="주요 활동 지역" value={team.main_location || team.district || "미입력"} />
              <TeamInfoRow label="주차"           value={team.parking ? `${team.parking}${team.parking_count ? ` (${team.parking_count}대)` : ""}` : "미입력"} />
            </div>

            {/* 팀 소개 */}
            <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>팀 소개</div>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
                {team.intro || "아직 팀 소개글이 없어요"}
              </div>
            </div>

            <button style={styles.modalCloseBtn} onClick={onClose}>닫기</button>
          </>
        )}
      </div>
      {viewerSrc && <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}
    </div>
  );
}

function TeamInfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

function EmptyState({ icon, text, sub }) {
  return (
    <div style={styles.emptyWrap}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 15, color: "#6b7280", fontWeight: 600 }}>{text}</div>
      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6, textAlign: "center", lineHeight: 1.6 }}>{sub}</div>
    </div>
  );
}

function ConfirmDeleteModal({
  onConfirm,
  onClose,
  title = "목록에서 삭제할까요?",
  body  = "취소/거절된 매칭을 목록에서 제거합니다.",
}) {
  return (
    <div style={styles.confirmBackdrop} onClick={onClose}>
      <div style={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 28, marginBottom: 10, textAlign: "center" }}>🗑</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: 6 }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginBottom: 20, lineHeight: 1.6 }}>
          {body}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.confirmNoBtn} onClick={onClose}>취소</button>
          <button style={styles.confirmYesBtn} onClick={onConfirm}>삭제하기</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmCancelModal({ onConfirm, onClose }) {
  return (
    <div style={styles.confirmBackdrop} onClick={onClose}>
      <div style={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 28, marginBottom: 10, textAlign: "center" }}>⚠️</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: 6 }}>
          정말 매칭을 취소하시겠어요?
        </div>
        <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginBottom: 20, lineHeight: 1.6 }}>
          취소 후에는 되돌릴 수 없습니다.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.confirmNoBtn} onClick={onClose}>아니오</button>
          <button style={styles.confirmYesBtn} onClick={onConfirm}>취소하기</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container:      { minHeight: "100vh", background: "#f7f8fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:         { padding: "calc(env(safe-area-inset-top) + 18px) 20px 16px", borderBottom: "1px solid #edf0f3", background: "#ffffff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  logoRow:        { position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  homeBtn:        { background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: "4px", lineHeight: 1 },
  logoCenterWrap: { position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "baseline", gap: 4 },
  logoText:       { fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  logoSub:        { fontSize: 12, color: "#9ca3af", fontWeight: 400 },
  headerTitle:    { fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  headerSub:      { fontSize: 13, color: "#b0b8c4", marginTop: 2 },
  teamNameRow:    { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 0" },
  myTeamBadge:    { fontSize: 14, fontWeight: 700, color: "#374151", background: "#ffffff", padding: "6px 14px", borderRadius: 999, border: "1px solid #edf0f3", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  refreshBtn:     { fontSize: 12, color: "#b0b8c4", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" },
  tabRow:         { display: "flex", padding: "0 20px", marginTop: 12, borderBottom: "1px solid #edf0f3", background: "#ffffff" },
  tab:            { flex: 1, padding: "11px 0", background: "none", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, transition: "color 0.15s", whiteSpace: "nowrap" },
  badge:          { fontSize: 11, fontWeight: 700, background: "#E24B4A", color: "#fff", borderRadius: 999, padding: "1px 7px", minWidth: 18, textAlign: "center" },
  listWrap:       { flex: 1, padding: "14px 20px 32px", display: "flex", flexDirection: "column", gap: 12 },
  groupWrap:      { display: "flex", flexDirection: "column", gap: 8 },
  postHeader:     { padding: "12px 14px", background: "#fff8f8", borderRadius: 12, border: "1px solid #fecaca", marginBottom: 4 },
  autoRejectNotice: { padding: "10px 14px", background: "#fffbeb", borderRadius: 10, border: "1px solid #fde68a", fontSize: 12, color: "#d97706" },
  autoRejectHint: { marginTop: 8, fontSize: 11, color: "#b0b8c4", lineHeight: 1.5 },
  card:           { background: "#ffffff", borderRadius: 16, border: "1px solid #edf0f3", padding: "18px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" },
  cardTop:        { display: "flex", alignItems: "center", gap: 12 },
  sportIcon:      { width: 42, height: 42, background: "#f7f8fa", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, border: "1px solid #edf0f3", overflow: "hidden" },
  cardTeamName:   { fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 2 },
  cardSub:        { fontSize: 12, color: "#b0b8c4" },
  statusBadge:    { padding: "4px 11px", borderRadius: 999, fontSize: 12, fontWeight: 700, flexShrink: 0 },
  actionRow:      { display: "flex", gap: 10, marginTop: 14 },
  acceptBtn:      { flex: 1, padding: "12px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  rejectBtn:      { padding: "12px 20px", background: "#f7f8fa", color: "#6b7280", border: "1px solid #edf0f3", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  cancelBtn:      { padding: "11px 20px", background: "#f7f8fa", color: "#6b7280", border: "1px solid #edf0f3", borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  acceptedMsg:    { marginTop: 12, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, fontSize: 12, color: "#16a34a", border: "1px solid #bbf7d0" },
  chatBtn:        { width: "100%", marginTop: 8, padding: "12px", background: "#111827", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  rejectedMsg:    { marginTop: 12, padding: "10px 14px", background: "#f7f8fa", borderRadius: 10, fontSize: 12, color: "#9ca3af", border: "1px solid #edf0f3" },
  pendingMsg:     { marginTop: 12, padding: "10px 14px", background: "#fffbeb", borderRadius: 10, fontSize: 12, color: "#d97706", border: "1px solid #fde68a" },
  emptyWrap:      { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center" },
  errorBox:       { display: "flex", alignItems: "center", margin: "12px 20px", padding: "12px 14px", background: "#fff5f5", borderRadius: 12, border: "1px solid #fecaca" },
  spinner:        { width: 32, height: 32, border: "3px solid #edf0f3", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  confirmBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, backdropFilter: "blur(4px)" },
  confirmBox:      { width: "86%", maxWidth: 300, background: "#fff", borderRadius: 16, padding: "24px 20px 20px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" },
  confirmNoBtn:    { flex: 1, padding: "11px", background: "#f7f8fa", color: "#374151", border: "1px solid #edf0f3", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  confirmYesBtn:   { flex: 1, padding: "11px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  modalBackdrop:      { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, backdropFilter: "blur(4px)" },
  teamModalBox:       { width: "90%", maxWidth: 340, background: "#fff", borderRadius: 16, padding: "18px 18px 22px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", maxHeight: "70vh", overflowY: "auto" },
  modalCloseBtn:      { width: "100%", padding: "11px", background: "#f7f8fa", color: "#374151", border: "1px solid #edf0f3", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  // 결과/매너 버튼
  resultBtn:          { width: "100%", marginTop: 8, padding: "11px", background: "#1e40af", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  resultWaitMsg:      { marginTop: 8, padding: "10px 14px", background: "#f0f9ff", borderRadius: 10, fontSize: 12, color: "#0369a1", border: "1px solid #bae6fd", textAlign: "center" },
  confirmedResultBox: { display: "flex", alignItems: "center", padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, border: "1px solid #bbf7d0", marginTop: 8, gap: 4 },
  viewResultBtn:      { marginLeft: "auto", fontSize: 11, color: "#16a34a", background: "none", border: "none", cursor: "pointer", fontWeight: 700, textDecoration: "underline" },
  mannerBtn:          { width: "100%", marginTop: 8, padding: "11px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  reviewDoneMsg:      { marginTop: 8, padding: "10px 14px", background: "#fffbeb", borderRadius: 10, fontSize: 12, color: "#d97706", border: "1px solid #fde68a", textAlign: "center" },
  deleteBtn:          { width: "100%", marginTop: 8, padding: "10px", background: "#f7f8fa", color: "#9ca3af", border: "1px solid #edf0f3", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" },
};
